import type { SiteTimeline, TimelineFishGroup } from '@/domain/fishGroups';

import type { BlockOperation, GroupRowModel, LineageBlock, RowRole, WeekAxis } from '../types';

import { cageChains } from './cageChains';
import { buildChainRow } from './chainRows';
import { transferFacts } from './transferFacts';
import { groupStart, positionOf } from './weekAxis';

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Groups linked by transfers inside the scope, as sets of ids (union-find). */
function connectedComponents(groups: TimelineFishGroup[], edges: [number, number][]) {
    const parent = new Map(groups.map((group) => [group.id, group.id]));
    const find = (id: number): number => {
        let root = id;
        while (parent.get(root) !== root) root = parent.get(root) ?? root;
        parent.set(id, root);
        return root;
    };
    for (const [a, b] of edges) parent.set(find(a), find(b));

    const components = new Map<number, number[]>();
    for (const group of groups) {
        const root = find(group.id);
        components.set(root, [...(components.get(root) ?? []), group.id]);
    }
    return [...components.values()];
}

/**
 * The groups each operation took fish from or gave fish to, so a redistribution's badge can
 * name the others: `peersOf(operation, group)` is every other group in it, by display name.
 * Groups outside the scope are named from the timeline's external groups when it has them.
 */
function operationPeers(
    timeline: SiteTimeline
): (operationId: number, groupId: number) => string[] {
    const names = new Map<number, string>();
    for (const group of [...timeline.externalGroups, ...timeline.groups]) {
        names.set(group.id, group.name);
    }
    const members = new Map<number, Set<number>>();
    for (const transfer of timeline.transfers) {
        if (!transfer.operation) continue;
        const ids = members.get(transfer.operation.id) ?? new Set<number>();
        ids.add(transfer.sourceFishGroupId);
        ids.add(transfer.destinationFishGroupId);
        members.set(transfer.operation.id, ids);
    }
    return (operationId, groupId) =>
        [...(members.get(operationId) ?? [])]
            .filter((id) => id !== groupId)
            .flatMap((id) => names.get(id) ?? [])
            .sort(collator.compare);
}

/**
 * Longest path from a root, computed once per group: repeated splits and merges make the same
 * ancestors reachable along many paths, so walking them per parent and per comparison is
 * exponential. A cycle in bad data counts its re-entry as a root.
 */
function memoisedDepth(parentsOf: Map<number, number[]>): (id: number) => number {
    const depths = new Map<number, number>();
    const visiting = new Set<number>();
    const depthOf = (id: number): number => {
        const known = depths.get(id);
        if (known !== undefined) return known;
        if (visiting.has(id)) return 0;
        visiting.add(id);
        const parents = parentsOf.get(id) ?? [];
        const depth = parents.length === 0 ? 0 : 1 + Math.max(...parents.map(depthOf));
        visiting.delete(id);
        depths.set(id, depth);
        return depth;
    };
    return depthOf;
}

export type BlockSortKey = 'pen' | 'name';
export type SortDirection = 'asc' | 'desc';

/** A block sorts by its first result (or standalone) row, which is what it collapses to. */
function sortKey(rows: GroupRowModel[], key: BlockSortKey): string {
    const anchor = rows.find((row) => row.role !== 'source') ?? rows[rows.length - 1];
    if (key === 'name') return anchor.name;
    return anchor.currentPen ?? anchor.penTags[anchor.penTags.length - 1]?.penName ?? anchor.name;
}

/** Lineage blocks move as one unit, so a source never leaves the result it produced. */
export function sortLineageBlocks(
    blocks: LineageBlock[],
    key: BlockSortKey,
    direction: SortDirection
): LineageBlock[] {
    const sign = direction === 'asc' ? 1 : -1;
    return [...blocks].sort(
        (a, b) => sign * collator.compare(sortKey(a.rows, key), sortKey(b.rows, key))
    );
}

/**
 * A block whose rows all feed each other (pen A feeds pen B, which later merges back into A's
 * row) has no row left once its sources are hidden; its rows with parents are results then.
 */
function withResults(roles: RowRole[], originCounts: number[]): RowRole[] {
    if (roles.some((role) => role !== 'source')) return roles;
    return roles.map((role, index) => (originCounts[index] > 0 ? 'result' : role));
}

/**
 * The board's rows, grouped into lineage blocks: a grading or merge puts its sources above
 * the groups it produced, so the block can collapse to just the results. Blocks are ordered by
 * pen, the design's default sort; a group with no lineage is a block of one. With
 * `combineChains`, groups that continue each other in the same pen share one row
 * (`cageChains`), and lineage runs between those rows.
 */
export function buildLineageBlocks(
    timeline: SiteTimeline,
    axis: WeekAxis,
    now: Date,
    combineChains = false
): LineageBlock[] {
    const inScope = new Map(timeline.groups.map((group) => [group.id, group]));
    const edges: [number, number][] = timeline.transfers
        .filter((t) => inScope.has(t.sourceFishGroupId) && inScope.has(t.destinationFishGroupId))
        .map((t) => [t.sourceFishGroupId, t.destinationFishGroupId]);

    const chains = combineChains
        ? cageChains(timeline.groups, edges)
        : timeline.groups.map((group) => [group.id]);
    // A row is keyed by its latest group.
    const rowOf = new Map<number, number>();
    for (const chain of chains) for (const id of chain) rowOf.set(id, chain[chain.length - 1]);

    const parentsOf = new Map<number, number[]>();
    const childrenOf = new Map<number, number[]>();
    for (const [source, destination] of edges) {
        const from = rowOf.get(source) ?? source;
        const to = rowOf.get(destination) ?? destination;
        if (from === to) continue;
        parentsOf.set(to, [...new Set([...(parentsOf.get(to) ?? []), from])]);
        childrenOf.set(from, [...new Set([...(childrenOf.get(from) ?? []), to])]);
    }
    const depthOf = memoisedDepth(parentsOf);
    const creatingTransfer = new Map(
        timeline.transfers
            .filter((t) => inScope.has(t.sourceFishGroupId))
            .map((t) => [t.destinationFishGroupId, t] as const)
    );
    const peersOf = operationPeers(timeline);

    const chainOf = new Map(chains.map((chain) => [chain[chain.length - 1], chain]));
    const groupsOf = (rowKey: number) =>
        (chainOf.get(rowKey) ?? [rowKey])
            .map((id) => inScope.get(id))
            .filter((group): group is TimelineFishGroup => group !== undefined);

    const facts = transferFacts(timeline);
    const blocks = connectedComponents(timeline.groups, edges).map((ids) => {
        const inBlock = new Set(ids);
        const rowKeys = [...new Set(ids.map((id) => rowOf.get(id) ?? id))]
            .map((key) => ({ key, groups: groupsOf(key) }))
            .filter((row) => row.groups.length > 0)
            .sort(
                (a, b) =>
                    depthOf(a.key) - depthOf(b.key) ||
                    (groupStart(a.groups[0])?.getTime() ?? 0) -
                        (groupStart(b.groups[0])?.getTime() ?? 0) ||
                    collator.compare(
                        a.groups[a.groups.length - 1].name,
                        b.groups[b.groups.length - 1].name
                    )
            );
        const originCounts = rowKeys.map(({ key }) => (parentsOf.get(key) ?? []).length);
        const roles = withResults(
            rowKeys.map(
                ({ key }, index): RowRole =>
                    (childrenOf.get(key) ?? []).length > 0
                        ? 'source'
                        : originCounts[index] > 0
                          ? 'result'
                          : 'standalone'
            ),
            originCounts
        );
        const rows = rowKeys.map(({ groups }, index) =>
            buildChainRow(groups, {
                axis,
                now,
                role: roles[index],
                originCount: originCounts[index],
                creatingOperation: creatingTransfer.get(groups[0].id)?.operation ?? null,
                peersOf,
            })
        );
        return {
            key: `block-${Math.min(...ids)}`,
            rows,
            hasSources: rows.some((row) => row.role === 'source'),
            operations: blockOperations(timeline, new Set(ids), axis),
            transfers: facts
                .filter((fact) => inBlock.has(fact.sourceId) && inBlock.has(fact.destinationId))
                .map((fact) => ({ ...fact, leftPct: positionOf(axis, fact.date) })),
            parentRows: new Map(rowKeys.map(({ key }) => [key, parentsOf.get(key) ?? []])),
            liveRows: new Set(rows.filter((row) => row.isLive).map((row) => row.groupId)),
        };
    });

    return sortLineageBlocks(blocks, 'pen', 'asc');
}

/** The block's operations, from its transfers: one per operation, with every group it touched. */
function blockOperations(
    timeline: SiteTimeline,
    ids: ReadonlySet<number>,
    axis: WeekAxis
): BlockOperation[] {
    const operations = new Map<number, BlockOperation>();
    for (const transfer of timeline.transfers) {
        const { sourceFishGroupId: source, destinationFishGroupId: destination } = transfer;
        if (!ids.has(source) || !ids.has(destination)) continue;
        const operation = operations.get(transfer.operation.id) ?? {
            key: `operation-${transfer.operation.id}`,
            leftPct: positionOf(axis, transfer.date),
            groupIds: [],
        };
        operation.groupIds = [...new Set([...operation.groupIds, source, destination])];
        operations.set(transfer.operation.id, operation);
    }
    return [...operations.values()];
}
